import { useContext, useId, useRef } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames/bind";
import AppGlobalsContext from "app/AppGlobalsContext";
import { NAVLOGS_SHOW_SLIDES_NUMBER } from "app/constants";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselSlideChange,
} from "app/components/ui/Carousel/Carousel";
import Typography from "app/pages/.shared/Typography/Typography";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { RecentlyViewedTrip, RecentlyViewedTripCard } from "./RecentlyViewedTripCard";
import { UpcomingTripPlaceholder } from "./UpcomingTripPlaceholder";
import styles from "./RecentlyViewedTrips.module.scss";

const cx = classNames.bind(styles);

const DESKTOP_RESOLUTIONS = [RESOLUTION.MEDIUM, RESOLUTION.LARGE, RESOLUTION.XLARGE];
const CARDS_PER_DESKTOP_ROW = 3;
const UPCOMING_TRIP_PICTURES = ["/picture/beach-placeholder.svg", "/picture/city-placeholder.svg"];
const PHOTO_SIZES = "(min-width: 1024px) 325px, (min-width: 768px) 45vw, 77vw";
const CAROUSEL_OPTIONS = { skipSnaps: true };

/** On desktop, a row holding fewer trips than cards is completed with "your next trip" cards, one picture per empty slot. */
const getEmptySlotPictures = (tripCount: number, isDesktop: boolean) => {
	const emptySlotCount = CARDS_PER_DESKTOP_ROW - tripCount;

	if (!isDesktop || emptySlotCount <= 0) {
		return [];
	}

	return UPCOMING_TRIP_PICTURES.slice(0, emptySlotCount);
};

export type RecentlyViewedTripsProps = {
	trips: RecentlyViewedTrip[];
};

/** "Recently viewed trips" section: a carousel of the products the user looked at, padded with placeholders on desktop. */
export const RecentlyViewedTrips = ({ trips }: RecentlyViewedTripsProps) => {
	const titleId = useId();
	const { publicPath, resolution } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const previousIndex = useRef(0);

	if (trips.length === 0) {
		return null;
	}

	const slidesPerView = NAVLOGS_SHOW_SLIDES_NUMBER[resolution] ?? CARDS_PER_DESKTOP_ROW;
	const isDesktop = DESKTOP_RESOLUTIONS.includes(resolution);
	const emptySlotPictures = getEmptySlotPictures(trips.length, isDesktop);

	const handleSlideChange = ({ index, source }: CarouselSlideChange) => {
		const isForward = index > previousIndex.current;
		previousIndex.current = index;

		if (source === "api") {
			return;
		}

		if (!isForward) {
			track("last_seen_products_previous_click");
			return;
		}

		const lastFullyVisibleTrip = trips[index + Math.floor(slidesPerView) - 1];

		track("last_seen_products_next_click", {
			product: {
				...lastFullyVisibleTrip,
				lastSeenProductType: lastFullyVisibleTrip?.quotationURL ? "quote" : "product",
			},
		});
	};

	return (
		<section
			aria-labelledby={titleId}
			className={cx("recently-viewed-trips")}
			data-testid="recently-viewed-trips"
		>
			<Typography
				id={titleId}
				component="h2"
				variant="xl3"
				variantMD="xl4"
				isBold
				className={cx("recently-viewed-trips__title")}
				data-testid="recently-viewed-trips-title"
			>
				<FormattedMessage id="listing.last.viewed.trip" />
			</Typography>

			<Carousel
				aria-labelledby={titleId}
				opts={CAROUSEL_OPTIONS}
				slidesPerView={slidesPerView}
				gap={24}
				bleed="var(--recently-viewed-trips-bleed)"
				clipMarginBlock="var(--space-s8)"
				onSlideChange={handleSlideChange}
			>
				<CarouselContent>
					{trips.map(trip => (
						<CarouselItem key={trip.id}>
							<RecentlyViewedTripCard trip={trip} photoSizes={PHOTO_SIZES} />
						</CarouselItem>
					))}

					{emptySlotPictures.map(picture => (
						<CarouselItem key={picture} aria-hidden="true">
							<UpcomingTripPlaceholder
								picture={`${publicPath}${picture}`}
								pictureSizes={PHOTO_SIZES}
							/>
						</CarouselItem>
					))}
				</CarouselContent>
			</Carousel>
		</section>
	);
};
